import { useCallback, useState } from "react";
import { useLocation } from "wouter";
import { useQuery } from "@tanstack/react-query";
import { useAuth } from "@/hooks/useAuth";
import { BillingApiError, getBillingConfig, startCheckout } from "@/lib/billing-api";
import { getAppUrl, isCrossDomainConfigured, isOnAppDomain } from "@/lib/auth-redirect";
import { rememberUpgradeIntent, type PaidPlanId } from "@/lib/upgrade-intent";

export const BILLING_CONFIG_QUERY_KEY = ["/api/billing/config"] as const;

/**
 * Starting a Plus/Premium upgrade, shared by the pricing page and settings.
 *
 * Signed in: the backend creates a Stripe Checkout Session bound to this account and we
 * send the tab there (same tab — Stripe returns to /billing/success, which confirms the
 * upgrade). Signed out: remember the plan and go sign in first; checkout resumes after.
 *
 * `checkoutEnabled` is the server's yes/no for THIS user (kill switch + staged-rollout
 * allowlist) — while it's no, upgrade buttons render disabled instead of failing on
 * click. It's display only: the server re-checks when a checkout is actually requested.
 */
export function useUpgradeCheckout(): {
  checkoutEnabled: boolean;
  configLoaded: boolean;
  startingPlan: PaidPlanId | null;
  error: string | null;
  beginUpgrade: (plan: PaidPlanId) => Promise<void>;
} {
  const { isAuthenticated, user } = useAuth();
  const [, setLocation] = useLocation();
  const [startingPlan, setStartingPlan] = useState<PaidPlanId | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Keyed by user: the answer is per-user during a restricted rollout, so one account's
  // "yes" must never be served from cache to another (e.g. after switching accounts).
  const { data: config, isSuccess } = useQuery({
    queryKey: [...BILLING_CONFIG_QUERY_KEY, user?.id ?? "signed-out"],
    queryFn: getBillingConfig,
    staleTime: 60_000,
  });

  const beginUpgrade = useCallback(
    async (plan: PaidPlanId) => {
      setError(null);

      if (!isAuthenticated) {
        rememberUpgradeIntent(plan);
        const loginPath = `/login?upgrade=${plan}`;
        // Sign-in must happen on the app origin, where the session is stored.
        if (isCrossDomainConfigured() && !isOnAppDomain()) window.location.assign(`${getAppUrl()}${loginPath}`);
        else setLocation(loginPath);
        return;
      }

      // Upgrades aren't open for this user: never send a checkout request. (The buttons
      // are disabled too; this covers every other path, e.g. the ?upgrade= resume.)
      if (!config?.checkoutEnabled) {
        setError(checkoutErrorMessage(new BillingApiError("", 503, "checkout_disabled")));
        return;
      }

      setStartingPlan(plan);
      try {
        const url = await startCheckout(plan);
        // Leave `startingPlan` set: the button keeps its spinner until the page unloads.
        window.location.assign(url);
      } catch (err) {
        setStartingPlan(null);
        setError(checkoutErrorMessage(err));
      }
    },
    [isAuthenticated, setLocation, config?.checkoutEnabled],
  );

  return {
    checkoutEnabled: config?.checkoutEnabled ?? false,
    configLoaded: isSuccess,
    startingPlan,
    error,
    beginUpgrade,
  };
}

function checkoutErrorMessage(err: unknown): string {
  if (err instanceof BillingApiError) {
    switch (err.code) {
      case "already_subscribed":
        return "You already have a paid plan — switch plans or manage billing from Settings.";
      case "checkout_disabled":
        return "Upgrades are paused for a moment. Please try again soon.";
      case "rate_limited":
        return "Too many attempts — please wait a few minutes and try again.";
      case "unauthenticated":
        return "Please sign in again to upgrade.";
      default:
        return err.message;
    }
  }
  return "We couldn't reach checkout. Check your connection and try again.";
}
